import React, { useEffect, useState } from 'react';
import { ExternalLink, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import logo from '../assets/images/logo-1.jpg';

const isFooterFeatureEnabled = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  return ['true', '1', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const Footer: React.FC = () => {
  const [apiFooterData, setApiFooterData] = useState<any>({});
  const location = useLocation();
  const { t, t_api } = useLanguage();
  const showCurve = true;

  const fetchFooterContent = () => {
    fetch(`/api/homepage/content?ts=${Date.now()}`, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        if (data.Footer) setApiFooterData(data.Footer);
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchFooterContent();

    const refreshFooter = () => fetchFooterContent();
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') fetchFooterContent();
    };

    window.addEventListener('focus', refreshFooter);
    window.addEventListener('pageshow', refreshFooter);
    window.addEventListener('footer-settings-updated', refreshFooter);
    document.addEventListener('visibilitychange', refreshOnVisible);

    return () => {
      window.removeEventListener('focus', refreshFooter);
      window.removeEventListener('pageshow', refreshFooter);
      window.removeEventListener('footer-settings-updated', refreshFooter);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, []);

  const footerData = {
    logo_url: apiFooterData.logo_image || '',
    description: t_api(apiFooterData.description, apiFooterData.description_ar) || 'Discover the perfect solution with our curated services tailored to your global needs.',
    address: t_api(apiFooterData.address, apiFooterData.address_ar) || '6606 Al Omran St, Muscat, Oman',
    phone: apiFooterData.phone || '+968 24 590592',
    email: apiFooterData.email || 'info@hamdanoman.com',
    social_links: {
      facebook: apiFooterData.social_facebook || '#',
      twitter: apiFooterData.social_twitter || '#',
      instagram: apiFooterData.social_instagram || '#',
      linkedin: apiFooterData.social_linkedin || '#'
    },
    quick_links: {
      1: t('nav.home'),
      2: t('nav.about'),
      3: t('nav.careers'),
      4: t('nav.group')
    },
    newsletter_desc: t_api(apiFooterData.newsletter_desc, apiFooterData.newsletter_desc_ar) || 'Stay updated with our latest offers and news.',
    staff_title: t_api(apiFooterData.staff_title, apiFooterData.staff_title_ar) || 'Staff',
    staff_link_label: t_api(apiFooterData.staff_link_label, apiFooterData.staff_link_label_ar) || 'Staff Login',
    staff_link_url: apiFooterData.staff_link_url || 'https://owa.hamdanoman.com/owa/auth/logon.aspx?replaceCurrent=1&url=https%3a%2f%2fowa.hamdanoman.com%2fowa%2f'
  };

  const showNewsletter = isFooterFeatureEnabled(apiFooterData.newsletter_enabled);
  const showStaff = isFooterFeatureEnabled(apiFooterData.staff_enabled);
  const footerGridClass = `grid gap-10 mb-8 md:grid-cols-2 ${
    showNewsletter && showStaff
      ? 'lg:grid-cols-[1.25fr_1fr_1.35fr_1.65fr_0.75fr] xl:grid-cols-[1.35fr_1fr_1.45fr_1.75fr_0.7fr]'
      : showNewsletter
        ? 'lg:grid-cols-[1.25fr_1fr_1.35fr_1.65fr] xl:grid-cols-[1.35fr_1fr_1.45fr_1.75fr]'
        : showStaff
          ? 'lg:grid-cols-[1.35fr_1fr_1.45fr_0.75fr] xl:grid-cols-[1.45fr_1fr_1.55fr_0.7fr]'
          : 'lg:grid-cols-[1.35fr_1fr_1.45fr] xl:grid-cols-[1.45fr_1fr_1.55fr]'
  }`;

  return (
    <footer className="relative z-30">
      {/* Wave Top Curve - Updated to match hero section */}
      {showCurve && (
        <svg
          className="w-full h-[60px] md:h-[80px]"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#000000"
            d="M0,0 C360,60 1080,60 1440,0 L1440,80 L0,80 Z"
          ></path>
        </svg>
      )}

      {/* Main Footer Content */}
      <div className="bg-[#000000] text-[rgb(163,163,163)] py-12">
        <div className="max-w-7xl mx-auto px-8 relative">
          <div className="container mx-auto px-4 relative z-20">
            {/* Top Grid Section */}
            <div className={footerGridClass}>
              {/* Company Info */}
              <div>
                <a href="/">
                  <img
                    src={footerData.logo_url || logo}
                    alt="Hamdan Trading Group Logo"
                    className="h-10 mb-4"
                  />
                </a>
                <p className="mb-4">
                  {footerData.description}
                </p>
                <div className="flex space-x-4">
                  <a href={footerData.social_links.facebook}><Facebook className="w-5 h-5 hover:text-white" /></a>
                  <a href={footerData.social_links.twitter}><Twitter className="w-5 h-5 hover:text-white" /></a>
                  <a href={footerData.social_links.instagram}><Instagram className="w-5 h-5 hover:text-white" /></a>
                  <a href={footerData.social_links.linkedin}><Linkedin className="w-5 h-5 hover:text-white" /></a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-bold text-white mb-4">{t('footer.quick_links')}</h4>
                <ul className="space-y-2">
                  <li><Link to="/" className="hover:text-white">{footerData.quick_links[1]}</Link></li>
                  <li><Link to="/about" className="hover:text-white">{footerData.quick_links[2]}</Link></li>
                  <li><Link to="/careers" className="hover:text-white">{footerData.quick_links[3]}</Link></li>
                  <li><Link to="/about?tab=group" className="hover:text-white">{footerData.quick_links[4]}</Link></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-lg font-bold text-white mb-4">{t('nav.contact')}</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm leading-relaxed">
                    <MapPin className="w-4 h-4 text-[rgb(163,163,163)] flex-shrink-0" />
                    <span>{footerData.address}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[rgb(163,163,163)] flex-shrink-0" />
                    <a href={`tel:${footerData.phone}`} className="hover:text-white">{footerData.phone}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[rgb(163,163,163)] flex-shrink-0" />
                    <a href={`mailto:${footerData.email}`} className="hover:text-white">{footerData.email}</a>
                  </li>
                </ul>
              </div>

              {/* Newsletter */}
              {showNewsletter && (
                <div className="lg:min-w-[300px]">
                  <h4 className="text-lg font-bold text-white mb-4">{t('footer.newsletter')}</h4>
                  <p className="mb-3">{footerData.newsletter_desc}</p>
                  <div className="flex max-w-[360px]">
                    <input
                      type="email"
                      placeholder={t('footer.email_placeholder')}
                      className="min-w-0 flex-1 px-4 py-2 rounded-l text-dark"
                    />
                    <button className="bg-[#F2BB13] text-black px-4 py-2 rounded-r transition">
                      {t('footer.subscribe')}
                    </button>
                  </div>
                </div>
              )}

              {/* Staff */}
              {showStaff && (
                <div className="lg:justify-self-end lg:pr-2">
                  <h4 className="text-lg font-bold text-white mb-4">{footerData.staff_title}</h4>
                  <a
                    href={footerData.staff_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium hover:text-white transition"
                  >
                    <ExternalLink className="w-4 h-4 flex-shrink-0" />
                    <span>{footerData.staff_link_label}</span>
                  </a>
                </div>
              )}
            </div>

            {/* Bottom Copyright */}
            <div className="border-t border-gray-700 pt-6 text-sm text-[rgb(163,163,163)]">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
                <div>
                  © 2025 Hamdan Trading Group LLC. {t('footer.all_rights')} | {t('footer.developed_by')}{' '}
                  <a
                    href="https://thirdainsmart.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition font-medium"
                  >
                    3rd Ain Smart Infotech
                  </a>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
                  <Link to="/job-portal/legal?type=terms" className="hover:text-white transition">
                    {t('legal.terms')}
                  </Link>
                  <Link to="/job-portal/legal?type=privacy" className="hover:text-white transition">
                    {t('legal.privacy')}
                  </Link>
                  <Link to="/job-portal/legal?type=cookie" className="hover:text-white transition">
                    {t('legal.cookie')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
